import Event from "../../../utils/event";
import {SYSTEM_EVENTS} from "../../../events/events";
import {TimeSpan} from "@sparkware/app-utils";
import {PLAYER_EVENT_TYPES} from "./main_proxy_consts";
import _ from '../../../utils/underscore';
import BaseObject from "../../../base/base_object";
import {FALLBACK_HANDLERS} from "../fallback/fallback_service_consts";
import {KICK_TYPE} from "../player_messages_consts";
import MainRestrictionParser from "./main_restriction_parser";


const REFRESH_EVENTS_INTERVAL = 20;

class MainProxyStrategy extends BaseObject {
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'MainProxyStrategy';


    init(params) {
        super.init(params);
        this.serviceFactory = params.serviceFactory;
        this._uri = params.uri || '/cgpapi/playerEvents/getplayerevents';
        this._refreshInterval = params.refreshInterval || REFRESH_EVENTS_INTERVAL;
        this._omgVersion = params.omgVersion;
        this._subscribe();
        this.isConnected = true;
        // We need it because geo need to know that firebase is ready. so in case we don't use firebase as in NJ we need to told him that we ready. temporary until NJ will move also to firebase
        Event.fire(SYSTEM_EVENTS.PLAYER_MESSAGES_STRATEGY_READY);
    }

    isConnected = false;


    _subscribe() {
        Event.on(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, () => {
            Event.fire(SYSTEM_EVENTS.PLAYER_MESSAGES_STRATEGY_READY);
            this._onStateChanged(this.serviceFactory.oAuth2Service().isAuthorized());
        });
        Event.on(SYSTEM_EVENTS.ON_INIT_COMPLETE, () => {
            this._onStateChanged(this.serviceFactory.oAuth2Service().isAuthorized());
        });
    }

    _onStateChanged(isAuthorized) {
        if (this._intervalObserver) {
            this._intervalObserver.dispose();
        }
        if (isAuthorized) {
            // start fallback to wallet and FP
            this.serviceFactory.fallbackService().start(FALLBACK_HANDLERS.WALLET);
            this._intervalObserver = this.serviceFactory.observableFactoryService().fromInterval(TimeSpan.fromSeconds(this._refreshInterval))
                .forEach(() => {
                    this._getPlayerMessages();
                });
            this.isConnected = true;
        } else {
            // stop fallback and pulling
            this.serviceFactory.fallbackService().stop();
            this.isConnected = false;
        }
    }

    disconnect() {
        return Promise.resolve().then(() => {
            this._intervalObserver?.dispose();
            this._intervalObserver = null;
            this.serviceFactory.fallbackService().stop();
            this.isConnected = false;
        })
    }

    _getPlayerMessages() {

        this.serviceFactory.http2Service().post(this._uri)
            .then((response) => {
                this.logger.debug('Received Player Events', JSON.stringify(response));
                this._readPlayerEvents(response.Events);
            })
            .catch((error) => {
               this.logger.error(error);
            });
    }

    _readPlayerEvents(playerEventsMessage) {
        // handle PlayerEvents
        if (playerEventsMessage) {
            this._handlePlayerEvents(playerEventsMessage)
        } else {
            this.logger.error('Player events data is null or undefined');
        }
    }


    _handlePlayerEvents(playerEvents) {
        if (!_.isArray(playerEvents) || playerEvents.length === 0) {
            this.logger.debug('There is no Player events to handle. PlayerEvents data is empty');
            return;
        }

        playerEvents.forEach((playerEvent) => {
            this._handlePlayerEvent(playerEvent);
        });
    }

    _handlePlayerEvent(playerEvent) {
        let eventType = playerEvent.EventType;
        let eventData = _.safeJSON(playerEvent.EventDataJson);
        if (!eventData) {
            this.logger.error(`EventData property is null or undefined!`);
            return;
        }
        switch (eventType) {
            case PLAYER_EVENT_TYPES.KICKED:
                this._onKickReceived(eventData);
                return;
            case PLAYER_EVENT_TYPES.KICKED_TO_LOBBY:
                // N/A
                return;
            case PLAYER_EVENT_TYPES.GEO_REQUIRED:
                this._onGeoReceived(eventData);
                return;
            case PLAYER_EVENT_TYPES.RESTRICTION_UPDATED:
                this._onRestrictionReceived(eventData);
                return;
            case PLAYER_EVENT_TYPES.OMG:
                this._onOMGReceived(eventData);
                return;
            default:
                this.logger.error(`Invalid player event type ${eventType} in event ${JSON.stringify(playerEvent)}`);
                return;
        }
    }


    _onKickReceived(data) {
        this.logger.info('Player event KICK received', JSON.stringify(data));
        Event.fire(SYSTEM_EVENTS.SESSION_EVENT_KICK, {KickType: KICK_TYPE.FULL_KICK, KickReasonId: data.KickReasonId + ''});
    }

    _onGeoReceived(data) {
        this.logger.info('Player event GEO received', JSON.stringify(data));
        Event.fire(SYSTEM_EVENTS.GEO_PERIODIC_UPDATE, {
            mobileNumber: data.Mobile,
            licenseKey: data.GeoComplyLicense,
            locationTransactionId: data.LocationTransactionId
        });
    }


    _onRestrictionReceived(data) {
        this.logger.info('Player event RESTRICTION UPDATE received', JSON.stringify(data));
        let parser = MainRestrictionParser.create();
        let restrictions = parser.parse(data);

        Event.fire(SYSTEM_EVENTS.SESSION_EVENT_UPDATERESTRICTION_RECEIVED, restrictions);

        //fire SYSTEM_EVENTS.GEO_RESTRICTIONS_UPDATE must happen after SYSTEM_EVENTS.SESSION_EVENT_UPDATERESTRICTION_RECEIVED
        Event.fire(SYSTEM_EVENTS.GEO_RESTRICTIONS_UPDATE, {KickReasonId: data?.KickReason?.toString(), DetailedMessages: data?.TroubleshooterMessages, TroubleshooterRetry: false});


    }
    
    _onOMGReceived(data) {
        this.logger.info('Player event OMG received', JSON.stringify(data));
        if (!this._isOMGCanBeDisplayed(data)) {
            this.logger.info('OMG can not be displayed because of OMG version is higher than supported one!');
            return;
        }

        if (data.OmgType === 2 || data.OmgType === 3) {
            this.logger.error(`Unsupported OMG type ${data.OmgType} received. Stop handle OMG message!`);
            return;
        }

        // standard OMG message
        Event.fire(SYSTEM_EVENTS.SESSION_EVENT_OMG_RECEIVED, {
            Messages: data.SerializedMessagesDetails,
            OMGType: data.OmgType,
            OpenedWithinGame: data.DisplayInGame ? '1' : '0'
        });
    }


    _isOMGCanBeDisplayed(message) {
        return this._omgVersion >= message.SupportVersion;
    }
}

export default MainProxyStrategy;